"use client";

import { useState } from "react";
import Link from "next/link";
import { ArrowDown, ArrowUp, ArrowUpRight, Minus, Square, ThumbsDown, ThumbsUp, type LucideIcon } from "lucide-react";
import { cn, formatMoney, formatNumber } from "@/lib/utils";
import { countryCodeToFlagEmoji } from "@/lib/countries";
import { ScreenImage } from "@/components/gallery/screen-image";
import { AppIconImage } from "@/components/ui/app-icon-image";
import { Button } from "@/components/ui/button";
import { countryCode } from "@/lib/security/input";
import { linksFor, singleApp, utcDay, type AskApp, type AskDesign, type AskMessage, type AskPageContext, type AskResult } from "./use-ask";

/** Questions the assistant offers when a chat is empty. */
export const SUGGESTIONS = [
  "Which are the best apps right now?",
  "Show me the paywall of the best fitness app",
  "Which apps are climbing fastest this week?",
  "Which apps are similar to Duolingo?",
  "Which new apps reached the charts this week?",
  "What are the top games in the US?",
  "Who hit #1 for the first time last month?",
  "Which apps are running the most ads?",
  "Top finance apps in the US right now",
  "Which top 25 apps make the most money?",
];

/** Questions about the app the user is looking at. */
export function appSuggestions(page: AskPageContext): string[] {
  if (!page.appId) return [];
  const name = page.appName || "this app";
  return [`How is ${name} ranking lately?`, `Which apps are similar to ${name}?`, `Show me ${name}'s onboarding and paywall screens`];
}

/** Follow-up questions for a finished answer, from the tools it used; each skips what a tool already showed. */
export function followUps(m: AskMessage): string[] {
  // A find_apps call counts as its list, so "Similar apps" is skipped once the similar list was read.
  const used = new Set(m.results.filter((r) => r.ok).map((r) => (r.name === "find_apps" ? `find_apps ${String(r.args.list)}` : r.name)));
  const out: [string, string?][] = [];
  if (singleApp(m)) out.push(["Show its paywall", "search_designs"], ["Similar apps", "find_apps similar"], ["Its revenue history", "get_revenue_history"]);
  if (used.has("find_apps search")) out.push(["Only the newest of these"], ["Sort by revenue"]);
  const chart = m.results.find((r) => r.ok && r.name === "find_apps" && r.args.list === "chart");
  if (chart) out.push(["Which of these make the most money?"], [`Same chart in the ${countryCode(chart.args.country) === "gb" ? "US" : "UK"}`]);
  return out.filter(([, tool]) => !tool || !used.has(tool)).map(([q]) => q).slice(0, 3);
}

/** Questions as chips; tapping one asks it. */
export function QuestionChips({ questions, onAsk }: { questions: string[]; onAsk: (q: string) => void }) {
  if (!questions.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {questions.map((q) => (
        <button key={q} type="button" onClick={() => onAsk(q)} className="inline-flex min-h-10 items-center rounded-full border border-border px-3 py-1.5 text-left text-xs hover:border-foreground/30 hover:bg-muted">
          {q}
        </button>
      ))}
    </div>
  );
}

const REASONS = ["Wrong app", "Wrong number", "Didn't answer"];

/** Thumbs up or down on one answer, sent with its trace id. A down vote offers a reason, sent as a second vote. */
export function AnswerFeedback({ trace }: { trace: string }) {
  const [vote, setVote] = useState<"up" | "down" | null>(null);
  const [reason, setReason] = useState<string | null>(null);
  const send = (v: "up" | "down", r?: string) => {
    setVote(v);
    setReason(r ?? null);
    void fetch("/api/ask/feedback", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ trace, vote: v, reason: r }) }).catch(() => {});
  };
  const thumb = (v: "up" | "down", Icon: LucideIcon, label: string) =>
    (!vote || vote === v) && (
      <button type="button" onClick={() => send(v)} disabled={!!vote} aria-label={label} aria-pressed={vote === v} className="grid h-10 w-10 place-items-center rounded-full hover:bg-muted hover:text-foreground disabled:text-foreground disabled:hover:bg-transparent">
        <Icon className={cn("h-4 w-4", vote === v && "fill-current")} />
      </button>
    );
  return (
    <div className="-ml-2.5 flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
      {thumb("up", ThumbsUp, "Good answer")}
      {thumb("down", ThumbsDown, "Bad answer")}
      {vote === "down" && !reason
        ? REASONS.map((r) => (
            <button key={r} type="button" onClick={() => send("down", r)} className="inline-flex min-h-10 items-center rounded-full border border-border px-3 hover:border-foreground/30 hover:bg-muted hover:text-foreground">
              {r}
            </button>
          ))
        : vote && <span>Thanks, noted</span>}
    </div>
  );
}

export const BRIEF_QUESTION = "Give me today's App Store brief: the top 5 apps and the 3 biggest climbers in the US.";

/** Positions gained since the previous chart; positive is up. */
const rankDelta = (a: AskApp) => (a.rank != null && a.prevRank != null ? a.prevRank - a.rank : null);

export function Delta({ app, className }: { app: AskApp; className?: string }) {
  const d = rankDelta(app);
  if (d == null) return app.rank != null && app.prevRank == null ? <span className={cn("text-xs font-semibold text-sky-500", className)}>NEW</span> : null;
  if (d === 0) return <Minus className={cn("h-3 w-3 text-muted-foreground", className)} aria-label="no change" />;
  const Up = d > 0;
  return (
    <span className={cn("inline-flex items-center gap-0.5 text-xs font-medium tabular-nums", Up ? "text-emerald-500" : "text-rose-500", className)}>
      {Up ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
      {Math.abs(d)}
    </span>
  );
}

export function AppIcon({ app, size = 36 }: { app: AskApp; size?: number }) {
  return app.icon ? (
    <AppIconImage src={app.icon} alt="" width={size} height={size} unoptimized className="shrink-0 rounded-[22%] border border-border/60 bg-muted" style={{ width: size, height: size }} />
  ) : (
    <span className="grid shrink-0 place-items-center rounded-[22%] bg-muted text-xs font-semibold" style={{ width: size, height: size }}>
      {app.name.slice(0, 1)}
    </span>
  );
}

/** One app as a row: rank and movement, icon, name, who and what it is, then its numbers. */
export function AppRow({ app, onNavigate }: { app: AskApp; onNavigate?: () => void }) {
  const released = app.released && utcDay(app.released);
  const facts = [
    app.downloads != null && `${formatNumber(app.downloads)} dl/mo`,
    app.revenue != null && `${formatMoney(app.revenue)}/mo`,
    app.rating != null && `★ ${app.rating.toFixed(1)}${app.ratings != null ? ` (${formatNumber(app.ratings)})` : ""}`,
    released && `Released ${released}`,
    app.extra,
  ].filter(Boolean) as string[];
  const who = [app.developer, app.category, app.country && app.country !== "us" ? `${countryCodeToFlagEmoji(app.country)} ${app.country.toUpperCase()}` : null].filter(Boolean).join(" · ");
  return (
    <Link href={app.href} onClick={onNavigate} className="group flex items-start gap-2.5 rounded-lg px-2 py-2 transition-colors hover:bg-muted/70">
      {app.rank != null && <span className="mt-2 w-7 shrink-0 text-right text-xs font-semibold tabular-nums text-muted-foreground">#{app.rank}</span>}
      <AppIcon app={app} size={36} />
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span className="min-w-0 flex-1 truncate text-[13px] font-medium leading-tight group-hover:underline">{app.name}</span>
          <Delta app={app} className="shrink-0" />
        </span>
        {who && <span className="mt-0.5 block truncate text-xs text-muted-foreground">{who}</span>}
        {facts.length > 0 && (
          <span className="mt-1 flex flex-wrap gap-1">
            {facts.map((f) => (
              <span key={f} className="rounded-md bg-muted px-1.5 py-0.5 text-xs font-medium tabular-nums text-foreground/80">
                {f}
              </span>
            ))}
          </span>
        )}
      </span>
    </Link>
  );
}

/** Phone screenshots from the design library, each linking to its app's designs. */
export function DesignGrid({ designs, onNavigate }: { designs: AskDesign[]; onNavigate?: () => void }) {
  if (!designs.length) return null;
  return (
    <ul className="grid grid-cols-3 gap-2 sm:grid-cols-4">
      {designs.map((d) => (
        <li key={d.image} className="min-w-0">
          <Link href={d.href} onClick={onNavigate} className="group block" title={[d.appName, d.screen].filter(Boolean).join(" · ")}>
            <span className="relative block aspect-[9/19.5] overflow-hidden rounded-xl border border-border bg-muted">
              <ScreenImage src={d.image} alt={d.screen ?? `${d.appName ?? "App"} screen`} sizes="(min-width: 640px) 140px, 30vw" className="object-cover object-top transition-transform group-hover:scale-[1.02]" />
            </span>
            <span className="mt-1 block truncate text-xs font-medium leading-tight">{d.appName}</span>
            {d.screen && <span className="block truncate text-xs text-muted-foreground">{d.screen}</span>}
          </Link>
        </li>
      ))}
    </ul>
  );
}

/** "Open on the site" chips for the pages an answer's data came from. */
export function SiteLinks({ results, onNavigate }: { results: AskResult[]; onNavigate?: () => void }) {
  const links = linksFor(results);
  if (!links.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {links.map((l) => (
        <Link key={l.href} href={l.href} onClick={onNavigate} className="inline-flex min-h-10 items-center gap-1 rounded-full border border-border px-3 py-2 text-xs font-medium hover:border-foreground/30 hover:bg-muted">
          {l.label}
          <ArrowUpRight className="h-3 w-3 text-muted-foreground" />
        </Link>
      ))}
    </div>
  );
}

export const DISCLAIMER = "Answers come from App Store Tracker data. Estimates are approximate.";

/** The round button of a question box: send, or stop while an answer streams. */
export function SendButton({ busy, disabled, onStop }: { busy: boolean; disabled: boolean; onStop: () => void }) {
  return busy ? (
    <Button type="button" size="pill" onClick={onStop} className="w-10 px-0 has-[>svg]:px-0" aria-label="Stop">
      <Square className="size-3 fill-current" />
    </Button>
  ) : (
    <Button type="submit" size="pill" disabled={disabled} className="w-10 px-0 has-[>svg]:px-0 disabled:opacity-30" aria-label="Send">
      <ArrowUp />
    </Button>
  );
}
